import React, { useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@mui/material';
import { MarketplaceListing } from '../types';
import { MONO, Pill, rarityHex, cyanBtnSx } from './cardKit';
import { cardTitle } from '../../NFTCollection/components/CardInspectModal';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedListing: MarketplaceListing | null;
  isProcessing: boolean;
  formatRarity: (rarity: string | undefined | null) => string;

  // balances
  userIncBalance: string;
  userEarnedIncBalance: string;
  hasConnectedWallet: boolean;

  // actions
  onPayUsingEarnedTokens: () => void;
  onPayUsingInternalWallet: () => void;
  onPayUsingConnectedWallet: () => void;
};

const toNum = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const paperSx = {
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.28)',
  // Set colour + image explicitly (not the `background` shorthand) so MUI's
  // dark elevation overlay is replaced, not left transparent.
  backgroundColor: '#0b1119',
  backgroundImage: 'linear-gradient(180deg, #0b1119, #070b11)',
  boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.85)'
} as const;

const payOutlineSx = {
  fontWeight: 800,
  color: '#38e8ff',
  borderColor: 'rgba(56, 232, 255, 0.5)',
  borderRadius: '9px',
  py: 1,
  '&:hover': {
    borderColor: '#38e8ff',
    backgroundColor: 'rgba(56, 232, 255, 0.08)'
  },
  '&.Mui-disabled': {
    color: 'rgba(140, 170, 190, 0.35)',
    borderColor: 'rgba(140, 170, 190, 0.18)'
  }
} as const;

/* On a phone the two chips sit side by side only if both balances fit, and
 * stack otherwise (their row wraps): halving a 320–390px dialog left a chip
 * narrower than a balance like "25022.50000 INC", which ran over the chip's
 * border and into the next one. From sm up they share the row equally, as
 * before. The unit may drop under the number (<wbr />; there was no break
 * between them), and a number that cannot fit even alone breaks rather than
 * spill. The same chip as ListNftModal's — keep the two alike. */
const BalanceChip = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      flex: { xs: '1 1 auto', sm: 1 },
      minWidth: 0,
      p: 1.25,
      borderRadius: '10px',
      border: '1px solid rgba(140, 170, 190, 0.16)',
      background: 'rgba(7, 11, 17, 0.5)'
    }}
  >
    <Typography
      sx={{
        font: `800 9px ${MONO}`,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#8ea3b3'
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.4,
        font: `900 15px ${MONO}`,
        color: '#e8f4fb',
        fontVariantNumeric: 'tabular-nums',
        overflowWrap: 'anywhere'
      }}
    >
      {value}
      <wbr />
      <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: '#5c6b78' }}>
        INC
      </Box>
    </Typography>
  </Box>
);

export const PurchaseNftModal: React.FC<Props> = ({
  open,
  onClose,
  selectedListing,
  isProcessing,
  formatRarity,
  userIncBalance,
  userEarnedIncBalance,
  hasConnectedWallet,
  onPayUsingEarnedTokens,
  onPayUsingInternalWallet,
  onPayUsingConnectedWallet
}) => {
  const priceNum = useMemo(
    () => toNum(selectedListing?.priceInINC || '0'),
    [selectedListing]
  );
  const internalBalNum = useMemo(() => toNum(userIncBalance), [userIncBalance]);
  const earnedBalNum = useMemo(
    () => toNum(userEarnedIncBalance),
    [userEarnedIncBalance]
  );

  const hasEnoughEarned = earnedBalNum >= priceNum;
  const hasEnoughInternal = internalBalNum >= priceNum;
  const canSubmit = !!selectedListing && !isProcessing;
  const rc = rarityHex(selectedListing?.nft.rarity);

  const busy = (label: string) =>
    isProcessing ? <CircularProgress size={20} color="inherit" /> : label;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: paperSx }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Typography
          sx={{
            font: `800 10px ${MONO}`,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: '#8ea3b3',
            mb: 2
          }}
        >
          Confirm purchase
        </Typography>

        {selectedListing && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2.5 }}>
            <Box
              sx={{
                width: 92,
                height: 92,
                flexShrink: 0,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                background:
                  'radial-gradient(120% 100% at 50% 30%, #1b3350, #0a0f16 74%)',
                border: '1px solid rgba(56, 232, 255, 0.16)'
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 42% 38%, ${rc}, #0b2740 72%)`,
                  boxShadow: `0 0 24px ${rc}55`
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: 18, color: '#e8f4fb' }}
              >
                {cardTitle(selectedListing.nft.name)}
              </Typography>
              {/* May wrap: beside the 92px art there is ~100px on a 320px
                  phone, and the pill with "INFLUENCER · INCRYPT" could not
                  shrink below ~117px, so the dialog's content scrolled
                  sideways. The type now drops under the pill instead. */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  mt: 1
                }}
              >
                <Pill color={rc}>{formatRarity(selectedListing.nft.rarity)}</Pill>
                <Typography
                  sx={{
                    font: `700 10px ${MONO}`,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#8ea3b3'
                  }}
                >
                  {selectedListing.nft.type} · {selectedListing.nft.faction}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {selectedListing && (
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: '12px',
              border: '1px solid rgba(255, 207, 64, 0.25)',
              background: 'rgba(255, 207, 64, 0.05)'
            }}
          >
            <Typography
              sx={{
                font: `900 30px ${MONO}`,
                color: '#ffcf40',
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1
              }}
            >
              {selectedListing.priceInINC}
              <Box
                component="span"
                sx={{ ml: 0.75, font: `700 13px ${MONO}`, color: '#8a7526' }}
              >
                INC
              </Box>
            </Typography>
            <Typography
              sx={{ mt: 0.75, font: `600 12px ${MONO}`, color: '#8ea3b3' }}
            >
              ~${selectedListing.priceInUSD} USD · sold by{' '}
              {selectedListing.seller.username}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <BalanceChip label="Earned tokens" value={userEarnedIncBalance} />
          <BalanceChip label="INC balance" value={userIncBalance} />
        </Box>

        {canSubmit && !hasEnoughEarned && (
          <Typography
            sx={{
              mb: 1.5,
              font: `600 12px ${MONO}`,
              color: '#ff8a8a'
            }}
          >
            Not enough earned tokens ({earnedBalNum.toFixed(5)} of{' '}
            {priceNum.toFixed(5)} INC) — use another method below.
          </Typography>
        )}

        <Typography
          sx={{ font: `600 11px/1.6 ${MONO}`, color: '#5c6b78' }}
        >
          Paying by anything other than earned tokens needs a little SOL for
          network fees. Purchases are final and cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, display: 'block' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={onPayUsingEarnedTokens}
            disabled={!canSubmit || !hasEnoughEarned}
            sx={{ ...cyanBtnSx, '&.Mui-disabled': { opacity: 0.4 } }}
          >
            {busy('Pay with Earned Tokens')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPayUsingInternalWallet}
            disabled={!canSubmit || !hasConnectedWallet || !hasEnoughInternal}
            sx={payOutlineSx}
          >
            {busy('Pay with INC Tokens')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPayUsingConnectedWallet}
            disabled={!canSubmit || !hasConnectedWallet}
            sx={payOutlineSx}
          >
            {busy('Pay with Connected Wallet')}
          </Button>
          <Button
            fullWidth
            onClick={onClose}
            disabled={isProcessing}
            sx={{ mt: 0.5, color: '#8ea3b3', fontWeight: 700 }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
